import { Content, List, Root as Tabs, Trigger } from '@radix-ui/react-tabs';
import {
  selectAvvenimentiKeys,
  selectBonus,
  selectNumEsitiVirtual,
  selectPossibileVincitaMax,
  selectPossibileVincitaMaxWithBonus,
  selectPossibileVincitaMin,
  selectPossibileVincitaMinWithBonus,
  selectPrezzo,
  selectQuotaTotale,
  selectSistema,
  selectSistemi,
  selectTipo,
} from './virtualTicketSelectors';
import { selectDefaultPuntata, selectTipo as selectTipoCarrello } from 'features/carrello/carrelloSelectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useEffect, useState } from 'react';

import { TipoVirtualTicket } from './utils/calcoloCarrello/types';
import { VirtualTicketRemoveAllItems } from './components/virtualTicketRemoveAllItems';
import { VirtualTicketSingolaMultipla } from './components/virtualTicketSingolaMultipla';
import { VirtualTicketSistema } from './components/virtualTicketSistema/VirtualTicketSistema';
import { getSliceByTipoTicket } from 'features/carrello/utils';
import { selectIsFullCart } from 'features/carrello/carrelloSelectors';
import styles from './VirtualTicket.module.scss';
import { ticketUpdated } from './virtualTicketSlice';
import { useFormattedValue } from './virtualTicketHooks';
import { useGetLabelsByKeys } from 'hooks/useLingUI';

export const VirtualTicket = () => {
  const dispatch = useAppDispatch();
  const isFullCart = useTypedSelector(selectIsFullCart);

  const tipo = useTypedSelector(selectTipo);
  const bonus = useTypedSelector(selectBonus);
  const prezzo = useTypedSelector(selectPrezzo);
  const sistema = useTypedSelector(selectSistema);
  const sistemi = useTypedSelector(selectSistemi);
  const numEsiti = useTypedSelector(selectNumEsitiVirtual);
  const quotaTotale = useTypedSelector(selectQuotaTotale);
  const possibileVincitaMin = useTypedSelector(selectPossibileVincitaMin);
  const possibileVincitaMax = useTypedSelector(selectPossibileVincitaMax);
  const possibileVincitaMinWithBonus = useTypedSelector(selectPossibileVincitaMinWithBonus);
  const possibileVincitaMaxWithBonus = useTypedSelector(selectPossibileVincitaMaxWithBonus);

  const [tabValue, setTabValue] = useState<string>(String(TipoVirtualTicket.Singola)); // perchè il componente Tabs di Radix ha come props una stringa per gestire il tab e l'enum è number

  const avvenimentiKeys = useTypedSelector(selectAvvenimentiKeys);

  useEffect(() => {
    // i tab sono sempre singola, multipla e sistema ma abbiamo un caso in cui si devono visualizzare sia multipla che sistema e quindi va selezionato per scelta un tab
    tipo === TipoVirtualTicket.MultiplaSistema
      ? setTabValue(sistema ? String(TipoVirtualTicket.Sistema) : String(TipoVirtualTicket.Multipla))
      : setTabValue(String(tipo));
  }, [sistema, tipo]);

  const formattedQuotaTotale = useFormattedValue(quotaTotale);
  const formattedPossibileVincitaMin = useFormattedValue(possibileVincitaMin);
  const formattedPossibileVincitaMax = useFormattedValue(possibileVincitaMax);
  const formattedPossibileVincitaMinWithBonus = useFormattedValue(possibileVincitaMinWithBonus!);
  const formattedPossibileVincitaMaxWithBonus = useFormattedValue(possibileVincitaMaxWithBonus!);
  const formattedPrezzo = useFormattedValue(prezzo);

  const [labelSingola, labelMultipla, labelSistema, labelSvuota] = useGetLabelsByKeys([
    'singola',
    'multipla',
    'sistema',
    'svuota',
  ]);

  const tipoCarrello = useTypedSelector(selectTipoCarrello);
  const defaultPuntata = useTypedSelector(selectDefaultPuntata);

  const { actions } = getSliceByTipoTicket(tipoCarrello);

  return (
    <div className={`${styles.container} ${!isFullCart ? styles.mini : ''}`}>
      <Tabs
        defaultValue={String(tabValue)}
        className={styles.root}
        value={tabValue}
        onValueChange={(value) => {
          dispatch(
            ticketUpdated({
              sistema: value === String(TipoVirtualTicket.Sistema) ? true : false,
            })
          );
        }}
      >
        {isFullCart && (
          <List className={styles.list}>
            {/** Tab Singolo */}
            <Trigger
              value={String(TipoVirtualTicket.Singola)}
              className={styles.tab}
              onClick={() => setTabValue(String(TipoVirtualTicket.Singola))}
              disabled={tipo !== TipoVirtualTicket.Singola}
            >
              {labelSingola}
              {tipo === TipoVirtualTicket.Singola && <span className={styles.number}>{`(${numEsiti})`}</span>}
            </Trigger>

            {/** Tab Multiplo */}
            <Trigger
              value={String(TipoVirtualTicket.Multipla)}
              className={styles.tab}
              onClick={() => {
                setTabValue(String(TipoVirtualTicket.Multipla));
                dispatch(actions.puntataSingolaMultiplaUpdated(defaultPuntata));
              }}
              disabled={
                (tipo as TipoVirtualTicket) !== TipoVirtualTicket.Multipla &&
                (tipo as TipoVirtualTicket) !== TipoVirtualTicket.MultiplaSistema
              }
            >
              {labelMultipla}
              {((tipo as TipoVirtualTicket) === TipoVirtualTicket.Multipla ||
                (tipo as TipoVirtualTicket) === TipoVirtualTicket.MultiplaSistema) && (
                <span className={styles.number}>{`(${numEsiti})`}</span>
              )}
            </Trigger>

            {/** Tab Sistema */}
            <Trigger
              value={String(TipoVirtualTicket.Sistema)}
              className={styles.tab}
              onClick={() => setTabValue(String(TipoVirtualTicket.Sistema))}
              disabled={
                (tipo as TipoVirtualTicket) !== TipoVirtualTicket.Sistema &&
                (tipo as TipoVirtualTicket) !== TipoVirtualTicket.MultiplaSistema
              }
            >
              {labelSistema}
              {((tipo as TipoVirtualTicket) === TipoVirtualTicket.Sistema ||
                (tipo as TipoVirtualTicket) === TipoVirtualTicket.MultiplaSistema) && (
                <span className={styles.number}>{`(${numEsiti})`}</span>
              )}
            </Trigger>

            {/** Tasto Svuota */}
            {avvenimentiKeys?.length > 0 && <VirtualTicketRemoveAllItems label={labelSvuota} />}
          </List>
        )}

        {/** Ticket Singolo */}
        <Content value={String(TipoVirtualTicket.Singola)} className={styles.content}>
          <VirtualTicketSingolaMultipla
            avvenimentiKeys={avvenimentiKeys}
            quotaTotale={formattedQuotaTotale}
            bonus={bonus}
            possibileVincitaMin={formattedPossibileVincitaMin}
            possibileVincitaMinWithBonus={formattedPossibileVincitaMinWithBonus}
          />
        </Content>

        {/** Ticket Multiplo */}
        <Content value={String(TipoVirtualTicket.Multipla)} className={styles.content}>
          <VirtualTicketSingolaMultipla
            avvenimentiKeys={avvenimentiKeys}
            quotaTotale={formattedQuotaTotale}
            bonus={bonus}
            possibileVincitaMin={formattedPossibileVincitaMin}
            possibileVincitaMinWithBonus={formattedPossibileVincitaMinWithBonus}
            isMultipla={true}
          />
        </Content>

        {/** Ticket Sistema */}
        <Content value={String(TipoVirtualTicket.Sistema)} className={styles.content}>
          <VirtualTicketSistema
            avvenimentiKeys={avvenimentiKeys}
            bonus={bonus}
            possibileVincitaMin={formattedPossibileVincitaMin}
            possibileVincitaMax={formattedPossibileVincitaMax}
            possibileVincitaMinWithBonus={formattedPossibileVincitaMinWithBonus}
            possibileVincitaMaxWithBonus={formattedPossibileVincitaMaxWithBonus}
            sistemi={sistemi}
            prezzo={formattedPrezzo}
          />
        </Content>
      </Tabs>
    </div>
  );
};
